import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db } from '../config/firebase';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';

function TaskDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTask = async () => {
      try {
        const snap = await getDoc(doc(db, 'tasks', id));
        if (snap.exists()) setTask({ id: snap.id, ...snap.data() });
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    if (id) fetchTask();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateDoc(doc(db, 'tasks', id), { status: newStatus, progress: newStatus === 'Completed' ? 100 : task.progress });
      setTask(prev => ({ ...prev, status: newStatus, progress: newStatus === 'Completed' ? 100 : prev.progress }));
    } catch (err) { console.error(err); }
  };

  const handleDelete = async () => {
    if (window.confirm('Delete this task?')) {
      await deleteDoc(doc(db, 'tasks', id));
      navigate('/admin/tasks');
    }
  };

  if (loading) return (
    <div className="flex h-screen overflow-hidden"><Sidebar /><div className="flex-1 flex items-center justify-center" style={{ marginLeft: isOpen ? '16rem' : '5rem' }}><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-golden"></div></div></div>
  );
  if (!task) return (
    <div className="flex h-screen overflow-hidden"><Sidebar /><div className="flex-1 flex items-center justify-center" style={{ marginLeft: isOpen ? '16rem' : '5rem' }}><div className="text-center"><p className="text-gray-500">Task not found</p><button onClick={() => navigate('/admin/tasks')} className="mt-4 text-brand-golden hover:underline">Back</button></div></div></div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-y-auto transition-all duration-300" style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}>
        <div className="p-8 max-w-3xl">
          <div className="flex items-center mb-6">
            {isMobile && (
              <button onClick={toggleMobileSidebar} className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg></button>
            )}
            <button onClick={() => navigate('/admin/tasks')} className="mr-4 text-gray-400 hover:text-brand-golden"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg></button>
            <h1 className="text-3xl font-bold text-gray-800">Task Details</h1>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{task.title}</h2>
            <p className="text-gray-600 mb-4">{task.description}</p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div><label className="text-xs font-semibold text-gray-400 uppercase">Status</label><span className="block mt-1 px-3 py-1 rounded-full text-sm font-medium inline-block" style={{backgroundColor: task.status === 'Completed' ? '#dcfce7' : task.status === 'In Progress' ? '#dbeafe' : '#fef9c3', color: task.status === 'Completed' ? '#166534' : task.status === 'In Progress' ? '#1e40af' : '#a16207'}}>{task.status}</span></div>
              <div><label className="text-xs font-semibold text-gray-400 uppercase">Priority</label><p className="text-gray-900 mt-1 font-medium">{task.priority}</p></div>
              <div><label className="text-xs font-semibold text-gray-400 uppercase">Deadline</label><p className="text-gray-900 mt-1">{task.deadline}</p></div>
              <div><label className="text-xs font-semibold text-gray-400 uppercase">Progress</label><div className="flex items-center gap-2 mt-1"><div className="flex-1 bg-gray-200 rounded-full h-2"><div className="h-2 rounded-full bg-blue-500" style={{width: `${task.progress || 0}%`}}></div></div><span className="text-sm font-medium">{task.progress || 0}%</span></div></div>
            </div>
            {task.tags?.length > 0 && <div className="mb-4"><label className="text-xs font-semibold text-gray-400 uppercase mb-2 block">Tags</label><div className="flex flex-wrap gap-2">{task.tags.map((tag, i) => <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md">#{tag}</span>)}</div></div>}
            {task.notes && <div className="mb-6"><label className="text-xs font-semibold text-gray-400 uppercase mb-2 block">Notes</label><p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{task.notes}</p></div>}
            <div className="flex gap-3 pt-4 border-t">
              {task.status !== 'Completed' && <button onClick={() => handleStatusChange('Completed')} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Mark Complete</button>}
              {task.status === 'Pending' && <button onClick={() => handleStatusChange('In Progress')} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Start Work</button>}
              <button onClick={handleDelete} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 ml-auto">Delete Task</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskDetails;
